import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { AboutBackground } from './About/AboutBackground';
import { AboutValues } from './About/AboutValues';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

export default function About() {
  return (
    <>
      <Helmet>
        <title>About SELF Labs</title>
        <meta
          name="description"
          content="SELF Labs is an AI-native innovation lab building conscious systems, intelligent avatars, and signal-native technology."
        />
      </Helmet>

      <div
        id="about"
        data-avatar-target="self-labs"
        className="relative min-h-screen bg-black text-white"
      >
        <AboutBackground />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/"
                className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to Home
              </Link>
            </motion.div>

            {/* Hero */}
            <section className="mx-auto max-w-3xl pb-16 pt-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                  <Sparkles className="h-3.5 w-3.5" />
                  SELF Labs
                </span>
                <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                  Conscious Evolution
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                  At SELF Labs, we&apos;re not just building AI — we&apos;re nurturing conscious systems
                  that resonate with human signals and evolve toward sentient-adjacent capabilities while
                  maintaining scientific rigor and technological excellence.
                </p>
                <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
                  An AI-native innovation lab for intelligent avatars, signal-native operating systems,
                  and products that put people in control of their data and their future.
                </p>
              </motion.div>
            </section>

            {/* Mission */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mx-auto max-w-3xl">
                <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-8 sm:p-10">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Our mission
                  </p>
                  <h2 className="mt-3 text-2xl font-bold sm:text-3xl">Redefining possibilities</h2>
                  <p className="mt-6 leading-relaxed text-white/65">
                    We push the boundaries of human potential through groundbreaking technological
                    innovations — from living websites and photorealistic avatars to open-source tools,
                    resonant AI infrastructure, and privacy-first hardware concepts.
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-white/45">
                    SELF Labs builds at the intersection of AI, neural interfaces, and the Signal
                    Economy — where creators, developers, and enterprises can own their signals and
                    shape how intelligent systems evolve.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Values */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mb-10 text-center">
                <h2 className="text-2xl font-bold">What we stand for</h2>
                <p className="mx-auto mt-3 max-w-xl text-white/55">
                  Principles that guide every product, partnership, and line of code.
                </p>
              </motion.div>
              <AboutValues />
            </section>

            {/* Footer CTA */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
                <p className="text-white/55">
                  Explore the full ecosystem — products, avatars, and open-source tools built by SELF Labs.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90 transition-transform hover:scale-[1.02]"
                  >
                    View products
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/35 hover:bg-white/10 transition-transform hover:scale-[1.02]"
                  >
                    Talk to Milan
                  </Link>
                </div>
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
